import { Box, Stack, styled } from '@mui/material';
import { Currency } from '@synergy/models';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { PoolFormattedMessage } from '@synergy/components-next';
import { PoolMath, shouldForwardPropExclude } from '@synergy/utils';
import { PoolRow, PoolTypography, useIsMobile } from '@synergy/components-next';
import { ReactElement } from 'react';

const shouldForwardProp = shouldForwardPropExclude('mobile');

const StyledRow = styled(PoolRow, { shouldForwardProp })<{ mobile: boolean }>(({ mobile, theme }) => ({
    borderBottom: '1px solid ' + theme.palette.grey[200],
    height: 26,
    paddingBottom: mobile ? 6 : 0,
    justifyContent: 'space-between',
}));

export interface SupplierTableRowProps {
    surcharges: number;
    deliveryCosts: number;
    totalNetPrice?: number;
    articleCosts?: number;
    currency: Currency;
    isSubTotal?: boolean;
}

export const PriceSum = (props: SupplierTableRowProps): ReactElement => {
    const { surcharges, deliveryCosts, totalNetPrice = 0, articleCosts = 0, currency, isSubTotal = false } = props;
    const isMobile = useIsMobile();

    return (
        <Stack typography="body2" width={isMobile ? 'auto' : 254} marginLeft={isMobile ? 0 : 'auto'} gap={0.75}>
            {isSubTotal && (
                <StyledRow typography="body2" mobile={isMobile}>
                    <PoolFormattedMessage id="app.cart.article.price.articles" />
                    <Box>
                        <FormattedNumber value={articleCosts} currency={currency} style="currency" />
                    </Box>
                </StyledRow>
            )}{' '}
            <PoolRow justifyContent="space-between" height={20}>
                <PoolFormattedMessage id="app.cart.article.price.surcharges" />
                <Box>
                    <FormattedNumber value={surcharges} currency={currency} style="currency" signDisplay="exceptZero" />
                </Box>
            </PoolRow>
            <StyledRow mobile={isMobile}>
                <PoolFormattedMessage id="app.cart.article.price.delivery" />
                <Box>
                    <FormattedNumber
                        value={deliveryCosts}
                        currency={currency}
                        style="currency"
                        signDisplay="exceptZero"
                    />
                </Box>
            </StyledRow>
            {!isSubTotal && (
                <strong>
                    <PoolRow justifyContent="space-between" height={20} gap={0}>
                        <PoolRow gap={0.25}>
                            <Box sx={{ textTransform: 'uppercase' }}>
                                <FormattedMessage id="app.cart.article.price.totalPrice" />
                            </Box>
                            <PoolTypography
                                typography="caption"
                                component="span"
                                translationId="app.cart.article.price.netto"
                                color="grey.600"
                            />
                        </PoolRow>
                        <Box>
                            <FormattedNumber value={totalNetPrice} currency={currency} style="currency" />
                        </Box>
                    </PoolRow>
                </strong>
            )}
            {isSubTotal && (
                <strong>
                    <PoolRow justifyContent="space-between" height={20}>
                        <Box>
                            <strong>
                                <FormattedMessage id="app.cart.article.price.subtotal" />
                            </strong>{' '}
                            <PoolTypography
                                typography="caption"
                                component="span"
                                translationId="app.cart.article.price.netto"
                                color="grey.600"
                                sx={{ fontSize: '.68rem' }}
                            />
                        </Box>
                        <Box>
                            <FormattedNumber
                                value={PoolMath.round(deliveryCosts + articleCosts + surcharges)}
                                currency={currency}
                                style="currency"
                            />
                        </Box>
                    </PoolRow>
                </strong>
            )}
        </Stack>
    );
};
